import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Stack, Typography } from "@mui/material";
import { differenceInCalendarDays, format, parseISO, subDays } from "date-fns";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { getDeadlineTotalDays, STAGE_COUNTDOWN_STAGES } from "../constants/deadlineRules";
import { SinisterStage } from "../constants/stageOptions";

interface Props {
	deadlineDate?: string | null;
	// No Pré Sinistro o prazo só corre enquanto o status atual for "collection_started" (Início da Atuação de
	// Cobrança) — é a janela até o operador registrar um novo status. Antes disso (caseStatus vazio,
	// deadlineDate ainda não existe) não há prazo aplicável. Depois que um status diferente é registrado,
	// a janela se encerra e só indicamos que ela foi cumprida, sem contagem.
	caseStatus?: string | null;
	// Etapa do sinistro. Na Desocupação (post_sinister) a contagem depende da etapa, não do status.
	stage?: SinisterStage | string | null;
	// Total de dias do prazo da etapa, quando a API devolve. Sem ele, usa a regra da etapa (ver deadlineRules).
	totalDays?: number | null;
}

// A data do prazo já vem pronta do backend (data de início + dias da etapa: 20 no Pré Sinistro, 30 na Desocupação).
// Aqui só calculamos o início, os dias restantes e a barra a partir do total de dias da etapa.
const COLLECTION_STARTED_STATUS = "collection_started";

// Degradê fixo representando a linha do tempo inteira (verde -> amarelo -> vermelho).
// A "máscara" cinza por cima cobre a parte que ainda não decorreu, revelando só o trecho já percorrido do gradiente.
const DEADLINE_GRADIENT = "linear-gradient(90deg, #4caf50 0%, #ffc107 65%, #f44336 100%)";

export const CreditRecoveryDeadlineIndicator = ({ deadlineDate, caseStatus, stage, totalDays }: Props) => {
	if (!deadlineDate) {
		return (
			<Typography variant="body2" color="text.secondary">
				—
			</Typography>
		);
	}

	const deadline = parseISO(deadlineDate);
	const totalDaysOfStage = getDeadlineTotalDays(stage, totalDays);
	const startDate = subDays(deadline, totalDaysOfStage);
	const daysRemaining = differenceInCalendarDays(deadline, new Date());
	const isStageCountdown = STAGE_COUNTDOWN_STAGES.includes(stage as SinisterStage);
	const startLabel = isStageCountdown ? "Início da etapa" : "Início da atuação";

	// Pré Sinistro: um status diferente de "collection_started" já foi registrado depois dele, a janela se
	// encerrou e só mostramos que ela foi cumprida — sem barra de progresso. Na Desocupação a contagem segue
	// a etapa, então o status do caso não encerra o prazo.
	if (!isStageCountdown && caseStatus !== COLLECTION_STARTED_STATUS) {
		return (
			<HtmlTooltip
				arrow
				placement="top"
				title={
					<Box>
						<Typography variant="subtitle2" fontWeight="bold">
							Prazo de cobrança
						</Typography>
						<Typography variant="body2">{startLabel}: {format(startDate, "dd/MM/yyyy")}</Typography>
						<Typography variant="body2">Prazo final: {format(deadline, "dd/MM/yyyy")}</Typography>
					</Box>
				}
			>
				<Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
					<CheckCircleOutlineIcon fontSize="small" color="success" />
					<Typography variant="caption" color="success.main" fontWeight={500}>
						Prazo cumprido
					</Typography>
				</Stack>
			</HtmlTooltip>
		);
	}

	const progressPercent = Math.min(100, Math.max(0, ((totalDaysOfStage - daysRemaining) / totalDaysOfStage) * 100));

	const color = daysRemaining < 0 ? "error" : daysRemaining <= 5 ? "warning" : "success";
	const label =
		daysRemaining < 0
			? `Vencido há ${Math.abs(daysRemaining)} dia${Math.abs(daysRemaining) === 1 ? "" : "s"}`
			: `${daysRemaining} dia${daysRemaining === 1 ? "" : "s"} restante${daysRemaining === 1 ? "" : "s"}`;

	return (
		<HtmlTooltip
			arrow
			placement="top"
			title={
				<Box>
					<Typography variant="subtitle2" fontWeight="bold">
						Prazo de cobrança
					</Typography>
					<Typography variant="body2">{startLabel}: {format(startDate, "dd/MM/yyyy")}</Typography>
					<Typography variant="body2">Prazo final: {format(deadline, "dd/MM/yyyy")}</Typography>
					<Typography variant="body2" color={`${color}.main`} fontWeight={500}>
						{label}
					</Typography>
				</Box>
			}
		>
			<Box minWidth={110}>
				<Stack spacing={0.4}>
					<Box position="relative" height={10} borderRadius={5} overflow="hidden" bgcolor="grey.200">
						<Box position="absolute" top={0} left={0} width="100%" height="100%" sx={{ background: DEADLINE_GRADIENT }} />
						<Box
							position="absolute"
							top={0}
							right={0}
							height="100%"
							width={`${100 - progressPercent}%`}
							bgcolor="grey.200"
							sx={{ transition: "width 0.3s ease" }}
						/>
					</Box>
					<Typography variant="caption" color={`${color}.main`} fontWeight={500}>
						{label}
					</Typography>
				</Stack>
			</Box>
		</HtmlTooltip>
	);
};
